/**
 * Shared browser audio for hub notifications — Phase 3.5 fix (2026-07-13).
 * Extended 2026-07-14 with a lightweight pub/sub so the inbox can render a
 * fallback banner while the AudioContext is still blocked by Chrome's
 * autoplay policy.
 *
 * Why a shared module?
 * Prior implementation created a fresh `AudioContext` every time an inbound
 * message arrived and called `oscillator.start()` immediately. Chrome (v66+)
 * refuses to auto-play audio until the tab has received a user gesture; the
 * context spawned from a WebSocket callback stays in state `"suspended"` and
 * silently produces zero audible output. Users heard nothing.
 *
 * The `previewNotifBeep()` used by the toggle worked because it ran INSIDE
 * a click handler → gesture context intact → context started `"running"`.
 * That masked the bug in every manual test but broke the real path.
 *
 * Fix strategy:
 *  1. One singleton `AudioContext` per tab, lazily created on the first
 *     access. Cheap to keep alive; browser suspends automatically when idle.
 *  2. `unlockAudio()` — resumes the singleton. Called from the "Sonido"
 *     toggle click in TopBar (which IS a user gesture) and from the
 *     "Habilitar" button in AudioReadinessBanner. After the first successful
 *     resume the context stays authorised for the whole session.
 *  3. `playNotifChime()` re-uses the singleton. It calls `resume()` again
 *     defensively (idempotent) in case the browser suspended the context
 *     while the tab was backgrounded. Failure is logged, never thrown.
 *  4. `subscribeAudioReadiness()` — components can observe the blocked
 *     state to render a "Habilitar sonido" banner. Backed by the native
 *     `AudioContext.onstatechange` event so no polling is needed.
 */

let _ctx = null;
const _listeners = new Set();

const isBlockedNow = () => !_ctx || _ctx.state !== "running";

const emitReadiness = () => {
    const blocked = isBlockedNow();
    _listeners.forEach((fn) => {
        try { fn(blocked); } catch (_e) { /* subscriber errors are their own problem */ }
    });
};

const getCtx = () => {
    if (_ctx) return _ctx;
    const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
    if (!AC) return null;
    try {
        _ctx = new AC();
        // Fire subscribers whenever the browser suspends/resumes the context
        // (e.g. tab hidden, first user gesture, page frozen). This is what
        // hides the banner automatically the moment audio becomes playable.
        _ctx.onstatechange = () => emitReadiness();
    } catch (e) {
        console.warn("[notif-sound] AudioContext construction failed:", e?.name || e);
        _ctx = null;
    }
    return _ctx;
};

/**
 * Called from a user-gesture handler to unblock the browser autoplay policy.
 * Safe to call many times; only the first non-suspended state matters.
 * Returns a promise that resolves when the resume() has been attempted.
 */
export const unlockAudio = async () => {
    const ctx = getCtx();
    if (!ctx) { emitReadiness(); return; }
    if (ctx.state === "suspended") {
        try { await ctx.resume(); }
        catch (e) { console.warn("[notif-sound] resume() rejected:", e?.name || e); }
    }
    // Notify subscribers regardless — onstatechange is fired async by the
    // browser and we want the UI to update on the same tick as the click.
    emitReadiness();
};

/**
 * Same waveform as the pre-fix beep (A5 sine, ~250 ms, ADSR-ish envelope)
 * so the audible result matches the toggle preview and users don't hear
 * a different sound in the real path vs the manual test.
 */
export const playNotifChime = async () => {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
        // Try one last resume() — if the user has interacted since we last
        // checked, this succeeds. If not, we still call start() so that any
        // later gesture-driven resume can flush the queued oscillator.
        try { await ctx.resume(); }
        catch { /* silent — will still attempt below */ }
    }
    if (ctx.state !== "running") {
        // Browser is still blocking audio. Warn once so we notice in devtools;
        // the fallback banner in the inbox is what surfaces this to the user.
        console.warn(
            "[notif-sound] chime skipped: AudioContext state=%s — user must " +
            "interact with the page before audio can play (autoplay policy)",
            ctx.state,
        );
        return;
    }
    try {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 880;   // A5
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.26);
    } catch (e) {
        console.warn("[notif-sound] chime playback failed:", e?.name || e);
    }
};

/**
 * Subscribe to audio-blocked changes. `fn(blocked: boolean)` is called
 * synchronously with the current state on subscribe, then again on every
 * subsequent state change (suspend/resume) or explicit `unlockAudio()`.
 * Returns an unsubscribe function.
 */
export const subscribeAudioReadiness = (fn) => {
    _listeners.add(fn);
    // Ensure a context exists so `onstatechange` starts firing. This is a
    // no-op if a chime has already been played, and it's cheap otherwise —
    // browsers suspend idle contexts automatically.
    getCtx();
    try { fn(isBlockedNow()); } catch (_e) { /* ignore */ }
    return () => { _listeners.delete(fn); };
};

/** Synchronous readout for one-shot checks (rarely useful; prefer the hook). */
export const isAudioBlocked = () => isBlockedNow();
